'use client'

import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { z } from 'zod'

import type { Locale } from '@helper/utils/types'
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@ui/web/components'
import { tryCatch } from '@helper/utils/core'
import cn from '@config/tailwind/cn'

const ChangePasswordFormSchema = (locale: Locale) =>
  z
    .object({
      oldPassword: z
        .string()
        .min(1, locale === 'id' ? 'Password saat ini wajib diisi' : 'Current password is required'),
      newPassword: z
        .string()
        .min(8, locale === 'id' ? 'Password minimal 8 karakter' : 'Password must be at least 8 characters')
        .refine(
          (val) => /[A-Z]/.test(val),
          locale === 'id'
            ? 'Password harus mengandung minimal 1 huruf besar'
            : 'Password must contain at least 1 uppercase letter',
        )
        .refine(
          (val) => /[a-z]/.test(val),
          locale === 'id'
            ? 'Password harus mengandung minimal 1 huruf kecil'
            : 'Password must contain at least 1 lowercase letter',
        )
        .refine(
          (val) => /[0-9]/.test(val),
          locale === 'id' ? 'Password harus mengandung minimal 1 angka' : 'Password must contain at least 1 number',
        )
        .refine(
          (val) => /[^A-Za-z0-9]/.test(val),
          locale === 'id'
            ? 'Password harus mengandung minimal 1 karakter khusus'
            : 'Password must contain at least 1 special character',
        ),
      confirmNewPassword: z
        .string()
        .min(1, locale === 'id' ? 'Konfirmasi password baru wajib diisi' : 'Confirm new password is required'),
    })
    .refine((data) => data.newPassword === data.confirmNewPassword, {
      message: locale === 'id' ? 'Passwords tidak sama' : 'Passwords do not match',
      path: ['confirmNewPassword'],
    })

export type ChangePasswordFormValues = z.infer<ReturnType<typeof ChangePasswordFormSchema>>

interface ChangePasswordFormProps {
  onSubmit: (values: ChangePasswordFormValues) => Promise<void> | void
  onLoadingChange?: (loading: boolean) => void
  submitButtonText?: string
  submitButtonClassName?: string
  className?: string
  locale?: Locale
  /** Hide the built-in title + description, e.g. when the surrounding dialog already has a header. */
  showHeader?: boolean
  /** Extra controls rendered to the left of the submit button. */
  footerActions?: ReactNode
  /** Class applied to the footer row that holds `footerActions` and the submit button. */
  footerClassName?: string
}

export function ChangePasswordForm({
  onSubmit,
  onLoadingChange,
  submitButtonText: submitButtonTextProp,
  submitButtonClassName,
  className,
  locale = 'id',
  showHeader = true,
  footerActions,
  footerClassName,
}: ChangePasswordFormProps) {
  const submitButtonText = submitButtonTextProp ?? (locale === 'id' ? 'Perbarui' : 'Update')

  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const form = useForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    } as z.input<ReturnType<typeof ChangePasswordFormSchema>>,
    validators: {
      onSubmit: ChangePasswordFormSchema(locale),
    },
    onSubmit: async ({ value, formApi }) => {
      onLoadingChange?.(true)
      const [_, error] = await tryCatch(async () => await onSubmit(value))
      onLoadingChange?.(false)

      if (error) {
        formApi.setErrorMap({
          // @ts-expect-error - TanStack Form errorMap accepts string for onSubmit
          onSubmit:
            error instanceof Error
              ? error.message
              : locale === 'id'
                ? 'Password gagal diubah'
                : 'Password failed to update',
        })
        return
      }

      formApi.reset()
    },
  })

  useEffect(() => {
    void form.validateAllFields('change')
  }, [])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const values = useStore(form.store, (state) => state.values)
  const requiredData = !values.oldPassword || !values.newPassword || !values.confirmNewPassword
  const disabled = !canSubmit || requiredData

  return (
    <div className={cn('srd:flex srd:flex-col srd:gap-4 srd:w-full', className)}>
      {showHeader && (
        <div className='srd:flex srd:flex-col srd:gap-2'>
          <h6 className='srd:text-lg srd:font-medium'>{locale === 'id' ? 'Pergantian password' : 'Password Change'}</h6>
          <p className='srd:text-sm'>
            {locale === 'id'
              ? 'Lakukan pergantian password untuk keamanan akunmu'
              : 'Perform password change for your account security'}
          </p>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
        className='srd:flex srd:flex-col srd:gap-6'
      >
        <FieldSet className='srd:flex srd:w-full srd:flex-col srd:gap-y-4' disabled={isSubmitting}>
          <form.Field name='oldPassword'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    {locale === 'id' ? 'Password saat ini' : 'Current password'}
                  </FieldLabel>
                  <InputGroup className='srd:bg-neutral-white srd:h-10'>
                    <InputGroupInput
                      id={field.name}
                      name={field.name}
                      placeholder={locale === 'id' ? 'Masukkan password kamu saat ini' : 'Enter your current password'}
                      value={field.state.value}
                      defaultValue={field.state.value}
                      type={showOldPassword ? 'text' : 'password'}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                      onBlur={field.handleBlur}
                    />
                    <InputGroupAddon align='inline-end'>
                      <InputGroupButton size='icon-xs' onClick={() => setShowOldPassword((p) => !p)} type='button'>
                        {showOldPassword ? (
                          <EyeOffIcon className='srd:size-4.5' />
                        ) : (
                          <EyeIcon className='srd:size-4.5' />
                        )}
                        <span className='srd:sr-only'>
                          {showOldPassword
                            ? locale === 'id'
                              ? 'Sembunyikan password'
                              : 'Hide password'
                            : locale === 'id'
                              ? 'Tampilkan password'
                              : 'Show password'}
                        </span>
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <form.Field name='newPassword'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Password baru' : 'New password'}</FieldLabel>
                  <InputGroup className='srd:bg-neutral-white srd:h-10'>
                    <InputGroupInput
                      id={field.name}
                      name={field.name}
                      placeholder={locale === 'id' ? 'Masukkan password baru kamu' : 'Enter your new password'}
                      value={field.state.value}
                      defaultValue={field.state.value}
                      type={showNewPassword ? 'text' : 'password'}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                      onBlur={field.handleBlur}
                    />
                    <InputGroupAddon align='inline-end'>
                      <InputGroupButton size='icon-xs' onClick={() => setShowNewPassword((p) => !p)} type='button'>
                        {showNewPassword ? (
                          <EyeOffIcon className='srd:size-4.5' />
                        ) : (
                          <EyeIcon className='srd:size-4.5' />
                        )}
                        <span className='srd:sr-only'>
                          {showNewPassword
                            ? locale === 'id'
                              ? 'Sembunyikan password'
                              : 'Hide password'
                            : locale === 'id'
                              ? 'Tampilkan password'
                              : 'Show password'}
                        </span>
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <form.Field name='confirmNewPassword'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field className='srd:gap-1 srd:w-full' data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    {locale === 'id' ? 'Konfirmasi password baru' : 'Confirm new password'}
                  </FieldLabel>
                  <InputGroup className='srd:bg-neutral-white srd:h-10'>
                    <InputGroupInput
                      id={field.name}
                      name={field.name}
                      placeholder={
                        locale === 'id'
                          ? 'Masukkan konfirmasi password baru kamu'
                          : 'Enter your new password confirmation'
                      }
                      value={field.state.value}
                      defaultValue={field.state.value}
                      type={showConfirmPassword ? 'text' : 'password'}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                      onBlur={field.handleBlur}
                    />
                    <InputGroupAddon align='inline-end'>
                      <InputGroupButton size='icon-xs' onClick={() => setShowConfirmPassword((p) => !p)} type='button'>
                        {showConfirmPassword ? (
                          <EyeOffIcon className='srd:size-4.5' />
                        ) : (
                          <EyeIcon className='srd:size-4.5' />
                        )}
                        <span className='srd:sr-only'>
                          {showConfirmPassword
                            ? locale === 'id'
                              ? 'Sembunyikan password'
                              : 'Hide password'
                            : locale === 'id'
                              ? 'Tampilkan password'
                              : 'Show password'}
                        </span>
                      </InputGroupButton>
                    </InputGroupAddon>
                  </InputGroup>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>
        </FieldSet>

        {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
          <p className='srd:text-error srd:text-sm'>{errorMap.onSubmit}</p>
        ) : null}

        <div className={cn('srd:flex srd:items-center srd:justify-end srd:gap-3', footerClassName)}>
          {footerActions}

          <Button
            type='submit'
            disabled={disabled}
            loading={Boolean(isSubmitting)}
            className={cn('srd:rounded-full srd:bg-blue-100 srd:text-neutral-white', submitButtonClassName)}
          >
            {submitButtonText}
          </Button>
        </div>
      </form>
    </div>
  )
}
